/**
 * PolicyCatalogList — the runtime policies as a DataTable (unframed, inside ListPage).
 * Template example: demo `/tables/data-tables` and NextAdmin `(dashboard)/(home)/_component/last-transactions-table`.
 */
import type React from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Badge, DataTable } from '../../../components/ui/index.js';
import type { BadgeVariant, DataTableColumn } from '../../../components/ui/index.js';
import type { ModelRuntimePolicy } from './types.js';
import { humanizeEnum } from './types.js';

export const policyPath = (id: number) => `/agents/libraries/model-runtime-policies/${id}`;

// ── Risk and status badges ──────────────────────────────────────────────────

const RISK_VARIANT: Record<ModelRuntimePolicy['riskTier'], BadgeVariant> = {
  low: 'success',
  medium: 'warning',
  high: 'danger',
  privileged: 'danger',
};

export function RiskBadge({ tier }: { tier: ModelRuntimePolicy['riskTier'] }) {
  return (
    <Badge variant={RISK_VARIANT[tier]}>{tier === 'privileged' ? 'Privileged' : `${humanizeEnum(tier)} risk`}</Badge>
  );
}

const STATUS_VARIANT: Record<ModelRuntimePolicy['status'], BadgeVariant> = {
  active: 'success',
  draft: 'warning',
  archived: 'neutral',
};

export function PolicyStatusBadge({ status }: { status: ModelRuntimePolicy['status'] }) {
  return (
    <Badge variant={STATUS_VARIANT[status]} dot={status === 'active'}>
      {humanizeEnum(status)}
    </Badge>
  );
}

// ── Limits and cost cells ───────────────────────────────────────────────────

function limitsText(policy: ModelRuntimePolicy): string {
  const turns = policy.maxTurns === 0 || policy.maxTurns == null ? 'Unlimited turns' : `${policy.maxTurns} turns`;
  const tools =
    policy.maxToolCalls === 0 || policy.maxToolCalls == null ? 'any tools' : `${policy.maxToolCalls} tool calls`;
  const timeout = policy.wallClockTimeoutMs != null ? `${(policy.wallClockTimeoutMs / 1000).toLocaleString()} s` : null;
  return [turns, tools, timeout].filter(Boolean).join(' · ');
}

function CostCell({ policy }: { policy: ModelRuntimePolicy }) {
  if (policy.spendCapUsd != null) return <Badge variant="danger">${policy.spendCapUsd} cap</Badge>;
  const tier = policy.costTier;
  if (!tier) return <span className="text-faint">—</span>;
  const variant: BadgeVariant = tier === 'free' || tier === 'low' ? 'success' : tier === 'high' ? 'danger' : 'warning';
  return <Badge variant={variant}>{humanizeEnum(tier)}</Badge>;
}

const COLUMNS: DataTableColumn<ModelRuntimePolicy>[] = [
  {
    key: 'policy',
    header: 'Policy',
    width: '28%',
    sortable: true,
    sortValue: (policy) => policy.name,
    render: (policy) => (
      <div>
        <Link
          to={policyPath(policy.id)}
          className="text-[13px] font-semibold text-heading transition-colors hover:text-accent-text"
          onClick={(event) => event.stopPropagation()}
        >
          {policy.name}
        </Link>
        <p className="mt-0.5 font-mono text-xs text-muted">
          {policy.slug}@{policy.version}
        </p>
        {policy.description && (
          <p className="mt-1 line-clamp-1 max-w-[340px] text-xs text-muted">{policy.description}</p>
        )}
      </div>
    ),
  },
  {
    key: 'models',
    header: 'Models',
    render: (policy) => (
      <span className="text-sm text-body">
        {policy.models.length > 0 ? policy.models.map((m) => m.displayName).join(' → ') : 'No models'}
      </span>
    ),
  },
  {
    key: 'purpose',
    header: 'Purpose',
    sortable: true,
    sortValue: (policy) => policy.purpose,
    render: (policy) => <span className="text-sm text-body">{humanizeEnum(policy.purpose)}</span>,
  },
  {
    key: 'risk',
    header: 'Risk',
    render: (policy) => (
      <div className="flex flex-col items-start gap-1">
        <RiskBadge tier={policy.riskTier} />
        {policy.riskTier === 'privileged' && <span className="text-xs text-muted">Chosen by name only</span>}
      </div>
    ),
  },
  {
    key: 'status',
    header: 'Status',
    render: (policy) => <PolicyStatusBadge status={policy.status} />,
  },
  {
    key: 'limits',
    header: 'Limits',
    render: (policy) => <span className="text-xs text-muted">{limitsText(policy)}</span>,
  },
  {
    key: 'cost',
    header: 'Cost',
    render: (policy) => <CostCell policy={policy} />,
  },
];

interface PolicyCatalogListProps {
  policies: ModelRuntimePolicy[];
  loading: boolean;
  empty: React.ReactNode;
  footer?: React.ReactNode;
}

export const PolicyCatalogList: React.FC<PolicyCatalogListProps> = ({ policies, loading, empty, footer }) => {
  const navigate = useNavigate();
  return (
    <DataTable
      columns={COLUMNS}
      rows={policies}
      rowKey={(policy) => policy.id}
      getRowAttention={(policy) => (policy.riskTier === 'privileged' ? 'danger' : undefined)}
      onRowClick={(policy) => navigate(policyPath(policy.id))}
      loading={loading}
      empty={empty}
      footer={footer}
      pageSize={20}
      aria-label="Runtime policies"
    />
  );
};
